/** The single pane: every vendor in scope, ranked by what is worth attention. */

import { useMemo, useState } from "react";
import { LedgerDial } from "../components/LedgerDial";
import { Section, StatTile } from "../components/Chrome";
import { SeverityBadge, TierBadge } from "../components/Badges";
import { money, moneyCompact, signedGap } from "../lib/format";
import type { Portfolio, Vendor } from "../lib/types";
import type { Severity } from "../lib/format";

type SortKey = "gap" | "exposure" | "spend" | "name";

const SORTS: { key: SortKey; label: string }[] = [
  { key: "gap", label: "Widest gap" },
  { key: "exposure", label: "Credit exposure" },
  { key: "spend", label: "Annual spend" },
  { key: "name", label: "Name" },
];

function sortVendors(vendors: Vendor[], key: SortKey): Vendor[] {
  const copy = [...vendors];
  switch (key) {
    case "name":
      return copy.sort((a, b) => a.name.localeCompare(b.name));
    case "spend":
      return copy.sort((a, b) => b.annual_spend - a.annual_spend);
    case "exposure":
      return copy.sort((a, b) => b.credits.computed - a.credits.computed);
    default:
      return copy.sort((a, b) => (b.gap ?? -Infinity) - (a.gap ?? -Infinity));
  }
}

/** The finding that makes the case: target met on the vendor's figure, missed on ours. */
export function headlineFinding(vendor: Vendor) {
  return vendor.findings.find(
    (finding) => finding.claimable && finding.reported_met && finding.computed_met === false,
  );
}

/**
 * How serious a vendor's position is, for the card badge.
 *
 * Materiality is decided server-side by engine.gap.is_material, so the badge here and
 * the count in the portfolio tile can never disagree.
 */
export function cardSeverity(vendor: Vendor): Severity {
  // `material` is decided server-side and already excludes unverifiable levels. The
  // claimable check is repeated here because this badge is what an audience reads, and
  // a single upstream regression should not be enough to put "Material gap" beside a
  // figure the platform never measured.
  if (vendor.findings.some((finding) => finding.claimable && finding.material)) return "high";
  const worst = vendor.findings
    .filter((finding) => finding.claimable)
    .sort((a, b) => (b.gap ?? 0) - (a.gap ?? 0))[0];
  return worst?.severity ?? "none";
}

function VendorCard({ vendor, onOpen }: { vendor: Vendor; onOpen: (id: string) => void }) {
  const headline = headlineFinding(vendor);

  return (
    <button
      onClick={() => onOpen(vendor.id)}
      className="card card--interactive"
      style={{
        padding: 20,
        textAlign: "left",
        background: "var(--panel)",
        color: "inherit",
        display: "grid",
        gridTemplateColumns: "minmax(0,1fr) auto",
        gap: 18,
        alignItems: "start",
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span className="serif" style={{ fontSize: 17 }}>
            {vendor.name}
          </span>
          <TierBadge tier={vendor.tier} />
          <SeverityBadge severity={cardSeverity(vendor)} />
        </div>
        <div style={{ fontSize: 12, color: "var(--slate)", marginTop: 4 }}>
          {vendor.category} · {money(vendor.annual_spend)}/yr · credit model{" "}
          {vendor.contract.credit_model}
        </div>

        <div style={{ marginTop: 14, fontSize: 12.5, lineHeight: 1.6, color: "var(--parchment)" }}>
          {headline ? (
            <span>
              <strong style={{ color: "var(--rust)" }}>{headline.service_level_name}</strong> reads as
              met on the vendor scorecard at {headline.reported}, and computes to {headline.computed}{" "}
              from {headline.record_count.toLocaleString()} records.
            </span>
          ) : (
            <span style={{ color: "var(--slate)" }}>
              No service level currently passes on the vendor's figure and fails on ours.
            </span>
          )}
        </div>

        <div style={{ display: "flex", gap: 22, marginTop: 14, flexWrap: "wrap" }}>
          <div>
            <div style={{ fontSize: 10, color: "var(--slate)", letterSpacing: "0.06em" }}>
              CREDITS COMPUTED
            </div>
            <div className="num serif" style={{ fontSize: 17, color: "var(--gold)" }}>
              {money(vendor.credits.computed)}
            </div>
          </div>
          <div>
            <div style={{ fontSize: 10, color: "var(--slate)", letterSpacing: "0.06em" }}>
              VENDOR CONCEDES
            </div>
            <div className="num serif" style={{ fontSize: 17, color: "var(--slate)" }}>
              {money(vendor.credits.reported)}
            </div>
          </div>
          <div>
            <div style={{ fontSize: 10, color: "var(--slate)", letterSpacing: "0.06em" }}>
              COMPOSITE GAP
            </div>
            <div
              className="num serif"
              style={{
                fontSize: 17,
                color: (vendor.gap ?? 0) > 3 ? "var(--rust)" : "var(--parchment)",
              }}
            >
              {signedGap(vendor.gap)} pts
            </div>
          </div>
        </div>
      </div>

      <LedgerDial
        reported={vendor.reported_composite}
        computed={vendor.computed_composite}
        size={124}
        label="composite"
      />
    </button>
  );
}

export function PortfolioView({
  portfolio,
  onOpenVendor,
}: {
  portfolio: Portfolio;
  onOpenVendor: (id: string) => void;
}) {
  const [sort, setSort] = useState<SortKey>("gap");
  const vendors = useMemo(() => sortVendors(portfolio.vendors, sort), [portfolio.vendors, sort]);
  const totals = portfolio.totals;

  return (
    <div>
      <Section
        title="Portfolio"
        subtitle={
          `Every figure below is computed from ${totals.records_ingested.toLocaleString()} records ` +
          `pulled from systems the client controls, then compared against what each vendor ` +
          `reported about itself for the same period.`
        }
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: 12,
          }}
        >
          <StatTile label="Vendors in scope" value={String(totals.vendors)} />
          <StatTile label="Annual spend" value={moneyCompact(totals.annual_spend)} />
          <StatTile
            label="Credits computed"
            value={money(totals.credits_computed)}
            note={`Vendors concede ${money(totals.credits_reported)}`}
            tone="gold"
          />
          <StatTile
            label="Understated"
            value={money(totals.credits_understated)}
            note={`Across ${totals.vendors_understated} vendor${totals.vendors_understated === 1 ? "" : "s"}`}
            tone="rust"
          />
          <StatTile
            label="Material gaps"
            value={String(totals.material_findings)}
            note="Verified service levels only"
            tone={totals.material_findings > 0 ? "rust" : "green"}
          />
        </div>
      </Section>

      <Section
        title="Vendors"
        subtitle="Ordered by the size of the difference between the vendor's account of itself and the records."
        actions={
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {SORTS.map((option) => (
              <button
                key={option.key}
                onClick={() => setSort(option.key)}
                aria-pressed={sort === option.key}
                style={{
                  background: "transparent",
                  border: `1px solid ${sort === option.key ? "var(--gold)" : "var(--line-strong)"}`,
                  color: sort === option.key ? "var(--gold)" : "var(--slate)",
                  borderRadius: 6,
                  padding: "5px 10px",
                  fontSize: 11.5,
                }}
              >
                {option.label}
              </button>
            ))}
          </div>
        }
      >
        <div style={{ display: "grid", gap: 12 }}>
          {vendors.map((vendor) => (
            <VendorCard key={vendor.id} vendor={vendor} onOpen={onOpenVendor} />
          ))}
        </div>
      </Section>
    </div>
  );
}
